// src/pages/Community/Missions/CharacterDetail.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./css/CharacterDetail.css";
import {
  LikeBar,
  ReactionBar,
  Bookmark,
  PresenceBadge,
  Discussion,
} from "../components";
import { UserName } from "../../../context/profileUI";
import FeedbackModal from "../components/FeedbackModal"; // <-- added

/* ---------- Helpers (must be defined before use) ---------- */

/**
 * Convert a YouTube url (youtu.be or youtube.com/...v=) into an embed URL.
 * Returns null when input doesn't look like a YouTube address.
 */
function toYouTubeEmbed(u = "") {
  if (!/youtu\.?be/.test(u)) return null;
  try {
    const normalized = u.replace("youtu.be/", "www.youtube.com/watch?v=");
    const url = new URL(normalized);
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    const last = url.pathname.split("/").filter(Boolean).pop();
    return last ? `https://www.youtube.com/embed/${last}` : null;
  } catch {
    return null;
  }
}

/**
 * Normalize Firestore document shape into the shape expected by the UI.
 * Keeps defensive checks so documents with a few missing fields don't crash the UI.
 */
function normalizeCharacterData(d = {}) {
  const clean = (v) => (typeof v === "string" ? v.trim() : v);

  // tags
  const tags = Array.isArray(d.tags)
    ? d.tags
    : d.tags
    ? String(d.tags)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const gallery = Array.isArray(d.gallery) ? d.gallery : [];

  // extract nested bio object
  const bioRaw = d.bio && typeof d.bio === "object" ? d.bio : {};
  const bio = {
    fullName: clean(bioRaw.fullName) || null,
    born: clean(bioRaw.born) || null,
    background: clean(bioRaw.background) || null,
    personality: clean(bioRaw.personality) || null,
    skills: clean(bioRaw.skills) || null,
    dreams: clean(bioRaw.dreams) || null,
    liking: clean(bioRaw.liking) || null,
    partner: clean(bioRaw.partner) || null, // Girlfriend/Wife
    children: clean(bioRaw.children) || null,
  };

  return {
    // Core identity (prefer explicit characterName, else bio.fullName)
    name: d.characterName ?? bio.fullName ?? d.name ?? "Unnamed",
    portrait: d.imageUrl ?? d.portrait ?? null,
    authorUid: d.authorUid ?? d.authorId ?? d.createdBy ?? d.userId ?? null,

    // Chips / quick facts
    role: d.role ?? d.roleAtCreation ?? "Character",
    status: d.status ?? null,
    firstAppearance: d.firstAppearance ?? null,
    affiliation: d.affiliation ?? d.game ?? null, // show game here

    // Text sections
    desc: clean(d.synopsis) || clean(d.story) || null, // short description under title
    synopsis: clean(d.synopsis) || null,
    storyLong: clean(d.story) || null,

    backstory: clean(d.backstory) || null,
    arc: clean(d.arc) || null,
    fate: clean(d.fate) || null,

    // Lists & media
    quotes: Array.isArray(d.quotes) ? d.quotes : [],
    relationships: Array.isArray(d.relationships) ? d.relationships : [],
    gallery,
    videoUrl: d.videoUrl ?? null,

    // More meta
    tags,
    voiceActor: d.voiceActor ?? null,
    age: typeof d.age === "number" ? d.age : d.age ? Number(d.age) : null,
    origin: d.origin ?? null,
    location: d.location ?? null,
    occupation: d.occupation ?? null,
    alias: d.alias ?? null,

    // structured bio fields
    bio,
  };
}

/**
 * Generic Section component used throughout the page.
 * Kept as a function declaration (not arrow) so it's hoisted and available to linting.
 */
function Section({ id, title, children }) {
  const headingId = title ? `${id}-h` : undefined;
  return (
    <section
      id={id}
      className="char-sec"
      {...(title ? { "aria-labelledby": headingId } : { "aria-label": id })}
    >
      {title && (
        <h2 className="char-h2" id={headingId}>
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

/* ---------- Component ---------- */

export default function CharacterDetail() {
  const { id } = useParams();
  const location = useLocation();

  const [c, setC] = useState(undefined); // undefined = loading, null = not found
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  // Feedback modal state
  const [fbOpen, setFbOpen] = useState(false); // <-- added

  // Lightbox
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const lightboxRef = useRef(null);

  const basePath = useMemo(() => ["characters", id], [id]);
  const bookmarkKey = useMemo(() => `characters:${id}`, [id]);

  /* ----- Data ----- */
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "characters", id), (snap) => {
      if (!snap.exists()) {
        setC(null);
        return;
      }
      const raw = snap.data();
      const normalized = normalizeCharacterData(raw);
      setC({ id: snap.id, ...normalized, _raw: raw });
    });
    return () => unsub();
  }, [id]);

  const yt = useMemo(
    () => (c?.videoUrl ? toYouTubeEmbed(c.videoUrl) : null),
    [c?.videoUrl]
  );

  /* ----- Share/Copy ----- */
  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname + location.hash;
    try {
      if (navigator.share) {
        await navigator.share({ title: c?.name ?? "Character", url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch {
      // no-op
    }
  }, [c?.name, location.pathname, location.hash]);

  /* ----- Lightbox ----- */
  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });

  const nextLight = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightbox((s) => {
        const len = c?.gallery?.length ?? 0;
        return { open: true, index: len ? (s.index + 1) % len : 0 };
      });
    },
    [c?.gallery?.length]
  );

  const prevLight = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightbox((s) => {
        const len = c?.gallery?.length ?? 0;
        return { open: true, index: len ? (s.index - 1 + len) % len : 0 };
      });
    },
    [c?.gallery?.length]
  );

  useEffect(() => {
    if (!lightbox.open) return;
    function onKey(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLight();
      if (e.key === "ArrowLeft") prevLight();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, nextLight, prevLight]);

  // Scroll to anchor if URL has hash
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, c]);

  const toc = useMemo(() => {
    if (!c) return [];
    const items = [];
    if (c.desc?.trim?.()) items.push({ id: "bio", label: "Description" });
    if (
      c.bio?.background ||
      c.bio?.personality ||
      c.bio?.skills ||
      c.bio?.dreams ||
      c.bio?.liking
    ) {
      items.push({ id: "biography", label: "Biography" });
    }
    if (
      c.storyLong?.trim?.() ||
      c.backstory?.trim?.() ||
      c.arc?.trim?.() ||
      c.fate?.trim?.()
    )
      items.push({ id: "story", label: "Story" });
    if (Array.isArray(c.quotes) && c.quotes.length > 0)
      items.push({ id: "quotes", label: "Notable Quotes" });
    if (Array.isArray(c.relationships) && c.relationships.length > 0)
      items.push({ id: "relationships", label: "Relationships" });
    if (Array.isArray(c.gallery) && c.gallery.length > 0)
      items.push({ id: "gallery", label: "Gallery" });
    if (c.videoUrl) items.push({ id: "video", label: "Video" });
    return items;
  }, [c]);

  /* ----- Loading / 404 ----- */
  if (c === undefined) {
    return (
      <div className="char-wrap">
        <div className="char-skel-card" aria-busy="true">
          <div className="char-skel-hero" />
          <div className="char-skel-title" />
          <div className="char-skel-row" />
          <div className="char-skel-section" />
        </div>
      </div>
    );
  }

  if (c === null) {
    return (
      <div className="char-wrap">
        <div className="char-empty-card" role="alert">
          <div className="char-empty-title">We can’t find that character</div>
          <p className="char-empty-sub">
            It may have been removed or the link is incorrect.
          </p>
          <Link
            to="/community/story-characters"
            className="char-btn char-btn--primary"
          >
            ← Back to Story & Characters
          </Link>
        </div>
      </div>
    );
  }

  /* ----- Render ----- */
  return (
    <>
      <div className="char-wrap">
        {/* Top bar */}
        <div className="char-detail-toprow">
          <Link
            to="/community/story-characters"
            className="char-back"
            aria-label="Back"
          >
            ← All characters
          </Link>

          <div className="char-actions">
            {c.videoUrl && !yt && (
              <a
                className="char-action"
                href={c.videoUrl}
                target="_blank"
                rel="noreferrer"
              >
                ▶ Open video
              </a>
            )}
            <button className="char-action" onClick={onShare}>
              ⤴ Share
            </button>

            {/* Feedback button (added) */}
            <button
              className="char-action"
              onClick={() => setFbOpen(true)}
              aria-label="Give feedback"
              title="Give feedback"
            >
              💬 Feedback
            </button>
          </div>
        </div>

        {/* Hero */}
        <header className="char-hero-row">
          <div className="char-hero-left">
            <div className="char-portrait-wrap">
              {c.portrait ? (
                <img
                  className="char-portrait"
                  src={c.portrait}
                  alt={`${c.name} portrait`}
                  loading="lazy"
                />
              ) : (
                <div
                  className="char-portrait char-portrait--placeholder"
                  aria-label="No image"
                >
                  🎭
                </div>
              )}
            </div>
          </div>

          {/* Right side */}
          <div className="char-hero-right">
            <div className="char-chips">
              {c.status && <span className="char-chip">📌 {c.status}</span>}
              {c.firstAppearance && (
                <span className="char-chip">🎬 {c.firstAppearance}</span>
              )}
              {c.affiliation && (
                <span className="char-chip">🏷 {c.affiliation}</span>
              )}
            </div>

            {/* creator */}
            {c.authorUid && (
              <div className="msn-meta char-byline">
                <span>
                  👤 Author{" "}
                  <strong>
                    <UserName userId={c.authorUid} fallback="Player" />
                  </strong>
                </span>
              </div>
            )}

            <h1 className="char-detail-title">{c.name}</h1>

            {selectedReactionFromBar && (
              <div className="msn-featured-reaction" aria-hidden>
                {(() => {
                  const id = selectedReactionFromBar;
                  const map = {
                    love: "❤️ Love",
                    fire: "🔥 Hot",
                    insightful: "🧠 Insightful",
                    cool: "🆒 Cool",
                    bug: "🐞 Issue",
                    respect: "🤝 Respect",
                    funny: "😂 Funny",
                    applause: "👏 Applause",
                  };
                  return map[id] || id;
                })()}
              </div>
            )}

            {/* Short description under the name; no header */}
            {c.desc?.trim?.() && (
              <Section id="bio">
                <div className="char-md char-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {c.desc}
                  </ReactMarkdown>
                </div>
              </Section>
            )}

            {/* Quick facts */}
            <div className="char-facts-grid">
              {c.bio?.fullName && (
                <div className="char-fact">
                  <span>Full Name</span>
                  <strong>{c.bio.fullName}</strong>
                </div>
              )}
              {c.affiliation && (
                <div className="char-fact">
                  <span>Affiliation / Game</span>
                  <strong>{c.affiliation}</strong>
                </div>
              )}
              {c.voiceActor && (
                <div className="char-fact">
                  <span>Voice Actor</span>
                  <strong>{c.voiceActor}</strong>
                </div>
              )}

              {c.bio?.born && (
                <div className="char-fact">
                  <span>Born</span>
                  <strong>{c.bio.born}</strong>
                </div>
              )}
              {c.age != null && (
                <div className="char-fact">
                  <span>Age</span>
                  <strong>{c.age}</strong>
                </div>
              )}
              {c.origin && (
                <div className="char-fact">
                  <span>Origin</span>
                  <strong>{c.origin}</strong>
                </div>
              )}

              {c.location && (
                <div className="char-fact">
                  <span>Location</span>
                  <strong>{c.location}</strong>
                </div>
              )}
              {c.occupation && (
                <div className="char-fact">
                  <span>Occupation</span>
                  <strong>{c.occupation}</strong>
                </div>
              )}
              {c.alias && (
                <div className="char-fact">
                  <span>Alias</span>
                  <strong>{c.alias}</strong>
                </div>
              )}

              {c.bio?.partner && (
                <div className="char-fact">
                  <span>Partner</span>
                  <strong>{c.bio.partner}</strong>
                </div>
              )}
              {c.bio?.children && (
                <div className="char-fact">
                  <span>Children</span>
                  <strong>{c.bio.children}</strong>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main layout */}
        <div className="char-grid-2">
          {/* Main content */}
          <main className="char-main">
            {/* Biography */}
            {(c.bio?.background ||
              c.bio?.personality ||
              c.bio?.skills ||
              c.bio?.dreams ||
              c.bio?.liking) && (
              <Section id="biography" title="Biography">
                {c.bio?.background && (
                  <>
                    <h3 className="char-h3">Background</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.bio.background}
                      </ReactMarkdown>
                    </div>
                  </>
                )}

                {c.bio?.personality && (
                  <>
                    <h3 className="char-h3">Personality</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.bio.personality}
                      </ReactMarkdown>
                    </div>
                  </>
                )}

                {c.bio?.skills && (
                  <>
                    <h3 className="char-h3">Skills</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.bio.skills}
                      </ReactMarkdown>
                    </div>
                  </>
                )}

                {c.bio?.dreams && (
                  <>
                    <h3 className="char-h3">Dreams</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.bio.dreams}
                      </ReactMarkdown>
                    </div>
                  </>
                )}

                {c.bio?.liking && (
                  <>
                    <h3 className="char-h3">Interests</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.bio.liking}
                      </ReactMarkdown>
                    </div>
                  </>
                )}
              </Section>
            )}

            {/* Story (long) + optional sub-sections */}
            {(c.storyLong?.trim?.() ||
              c.backstory?.trim?.() ||
              c.arc?.trim?.() ||
              c.fate?.trim?.()) && (
              <Section id="story" title="Story">
                {c.storyLong?.trim?.() && (
                  <div className="char-md char-prose">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {c.storyLong}
                    </ReactMarkdown>
                  </div>
                )}

                {c.backstory?.trim?.() && (
                  <>
                    <h3 className="char-h3">Backstory</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.backstory}
                      </ReactMarkdown>
                    </div>
                  </>
                )}

                {c.arc?.trim?.() && (
                  <>
                    <h3 className="char-h3">Character Arc</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.arc}
                      </ReactMarkdown>
                    </div>
                  </>
                )}

                {c.fate?.trim?.() && (
                  <>
                    <h3 className="char-h3">Fate</h3>
                    <div className="char-md char-prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {c.fate}
                      </ReactMarkdown>
                    </div>
                  </>
                )}
              </Section>
            )}

            {/* Quotes */}
            {Array.isArray(c.quotes) && c.quotes.length > 0 && (
              <Section id="quotes" title="Notable Quotes">
                <ul className="char-quote-list">
                  {c.quotes.map((q, i) => (
                    <li key={i} className="char-quote">
                      “{q}”
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Relationships */}
            {Array.isArray(c.relationships) && c.relationships.length > 0 && (
              <Section id="relationships" title="Relationships">
                <div className="char-rel">
                  {c.relationships.map((r, i) => (
                    <div
                      key={i}
                      className="char-relchip"
                      title={r.type || "Relationship"}
                    >
                      {r.characterId ? (
                        <Link
                          to={`/community/story-characters/${r.characterId}`}
                        >
                          {r.name}
                        </Link>
                      ) : (
                        <span>{r.name}</span>
                      )}
                      {r.type && (
                        <span className="char-reltype"> • {r.type}</span>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Gallery */}
            {Array.isArray(c.gallery) && c.gallery.length > 0 && (
              <Section id="gallery" title="Gallery">
                <div className="char-gallery">
                  {c.gallery.map((src, i) => (
                    <button
                      key={i}
                      className="char-shotbtn"
                      onClick={() => openLightbox(i)}
                      aria-label={`Open image ${i + 1}`}
                    >
                      <img
                        className="char-shot"
                        src={src}
                        alt={`Image ${i + 1}`}
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {/* Video */}
            {c.videoUrl && (
              <Section id="video" title="Video">
                {yt ? (
                  <div className="char-video">
                    <iframe
                      title="Character video"
                      src={yt}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <a
                    className="char-link"
                    href={c.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    ▶ Open video
                  </a>
                )}
              </Section>
            )}
          </main>

          {/* Full-width engagement (spans both columns) */}
          <div className="char-engage-full" role="region" aria-label="Engagement">
            <div className="msn-engage">
              <div className="char-engage-left">
                <PresenceBadge presenceKey="story_characters" entityId={id} />
                <LikeBar basePath={basePath} />
                <ReactionBar
                  basePath={basePath}
                  visibleReactions={[
                    "love",
                    "fire",
                    "insightful",
                    "cool",
                    "bug",
                    "respect",
                    "funny",
                    "applause",
                  ]}
                  highlight={null}
                  onSelect={(rid) => {
                    setSelectedReactionFromBar(rid);
                  }}
                />
              </div>
            </div>

            <div className="char-discussion-wrap">
              <Discussion basePath={basePath} />
            </div>
          </div>
          
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && Array.isArray(c.gallery) && c.gallery.length > 0 && (
        <div
          ref={lightboxRef}
          className="char-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <button
            className="char-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ✕
          </button>
          <button
            className="char-lightbox-nav left"
            onClick={prevLight}
            aria-label="Previous"
          >
            ‹
          </button>
          <img
            className="char-lightbox-img"
            src={c.gallery[lightbox.index]}
            alt={`Image ${lightbox.index + 1}`}
          />
          <button
            className="char-lightbox-nav right"
            onClick={nextLight}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      )}

      {/* Feedback modal (added) */}
      <FeedbackModal
        open={fbOpen}
        onClose={() => setFbOpen(false)}
        character={c}
        sourceComponent="CharacterDetail"
        source={location.pathname}
      />
    </>
  );
}
